// src/hooks/useLeadExtractor.ts
import { useCallback } from "react";

import type { ExtractedLeadV2 } from "../types/lead";

type UseLeadExtractorArgs = {
  orgId?: string | null;
};

const FALLBACK_SCHEMA: ExtractedLeadV2 = {
  first_name: null,
  last_name: null,
  phone: null,
  email: null,
  product_line: null,
  product_subtype: null,
  size_config: null,
  size_options: null,
  product_interest_category: null,
  lead_source_category: null,
  lead_source_detail: null,
  enunciated_budget: null,
  interest_level: null,
  ready_to_buy_timeline: null,
  customer_city_state: null,
  install_address_full: null,
  install_address_street: null,
  install_address_city: null,
  install_address_state: null,
  install_address_postal_code: null,
  billing_address: null,
  notes: null,
  distance_from_lot_miles: null,
  distance_from_plant_miles: null,
  nearest_steel_plant_name: null,
  nearest_steel_plant_distance_miles: null,
  interest_tags: null,
};

export function useLeadExtractor({ orgId }: UseLeadExtractorArgs) {
  const extract = useCallback(
    async (transcript: string): Promise<ExtractedLeadV2> => {
      try {
        const res = await fetch("/api/lead/extract", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ transcript, ...(orgId ? { orgId } : {}) }),
        });

        if (!res.ok) {
          throw new Error(`Lead extraction failed (${res.status})`);
        }

        const data: any = await res.json();
        const result = data?.result as ExtractedLeadV2 | undefined;

        if (!result || typeof result !== "object") {
          throw new Error("Invalid extraction response");
        }

        return result;
      } catch (err) {
        console.error("Extraction error", err);

        return {
          ...FALLBACK_SCHEMA,
          product_line: "Sheds",
          interest_level: "warm",
          ready_to_buy_timeline: "unknown",
          notes: (transcript || "").slice(0, 800),
        };
      }
    },
    [orgId]
  );

  const extractBulk = useCallback(async (transcript: string): Promise<Array<{ transcript: string; confidence: "high" | "medium" | "low" }>> => {
    const res = await fetch("/api/lead/extract", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ transcript, mode: "bulk", ...(orgId ? { orgId } : {}) }),
    });
    if (!res.ok) throw new Error(`Bulk extraction failed (${res.status})`);
    const data = (await res.json()) as { candidates?: Array<{ transcript: string; confidence: "high" | "medium" | "low" }> };
    return Array.isArray(data.candidates) ? data.candidates : [];
  }, [orgId]);

  return { extract, extractBulk };
}

